import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IEvent } from "../../interfaces/models/IEvent";
import {
  EventTypeEnum,
  EventHostSponsorEnum,
  SetupTypeEnum,
  getSetupTypeDisplay,
  StatusEnum,
} from "../../Enums/EventEnums";

import { UseRegionList } from "../../hooks/Catalogs/useRegionListOptions";
import { UseCountryList } from "../../hooks/Catalogs/useCountry";
import ShapeEdit from "../../components/Atomos/Icons/ShapeEdit";
import { formatUnixTimestampToUtcDateString } from "../../utils/functions";
import PersistentLink from "../../components/Moleculas/PersistentLink";
import { getAllFirmNames } from "../../Services/Firm";
import { firmShortLabel } from "../../utils/firmDisplayName";
import { getAllGroups } from "../../Services/Group";
import { getAllPracticeIndustries } from "../../Services/PracticeIndustry";
import Axios from "../../utils/axios";
import { ModifyEvent, NewEvent, ResetEvent } from "../../redux";
import { useDispatch } from "react-redux";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import type { IFilterData, IQuickFilter } from "../../components/Organismos/Table/FilteredTable.types";
import Button from "../../components/staff/Button";
import Plus from "../../components/Atomos/Icons/EditMode/Plus";
import { formatEventLocation, isVirtualLocationEvent } from "../../utils/eventLocationFormatter";
import { formatEventDate } from "../../utils/eventDateFormatter";
import {
  EVENT_YEAR_FILTER_PAST_ALL,
  EVENT_YEAR_FILTER_UPCOMING_ALL,
  getEventYearFilterOptions,
  getEventYearFilterTimingHint,
} from "../../utils/eventYearFilterOptions";

const listEventsAxios = new Axios();

function sponsorIdsEqual(a: unknown, b: unknown): boolean {
  return a != null && b != null && String(a) === String(b);
}

function normalizeSponsorType(type: unknown): string {
  if (type == null) {
    return "";
  }
  return String(type).trim().toLowerCase().replace(/\s+/g, "_");
}

type ResolveSponsorCtx = {
  firmData: any[] | undefined;
  allGroups: any[];
  allIndustries: any[];
};

function resolveSponsorEntryLabel(
  sponsor: { type?: unknown; id?: string },
  ctx: ResolveSponsorCtx
): string | null {
  if (sponsor?.id == null || String(sponsor.id) === "") {
    return null;
  }
  const t = normalizeSponsorType(sponsor.type);
  if (t === "member") {
    const f = ctx.firmData?.find((x: { id?: string }) => sponsorIdsEqual(x?.id, sponsor.id));
    const n = firmShortLabel(f);
    if (n != null && String(n).trim() !== "") {
      return String(n);
    }
    return null;
  }
  if (t === "wsg_group") {
    const g = ctx.allGroups.find((x: { id?: string }) => sponsorIdsEqual(x?.id, sponsor.id));
    const n = g?.title ?? g?.name;
    if (n != null && String(n).trim() !== "") {
      return String(n);
    }
    return null;
  }
  if (t === "industry") {
    const i = ctx.allIndustries.find((x: { id?: string }) => sponsorIdsEqual(x?.id, sponsor.id));
    if (i?.name != null) {
      return String(i.name);
    }
    return null;
  }
  if (t === "guest_sponsor" || t === "guestsponsor") {
    return String(sponsor.id);
  }
  return null;
}

function formatSponsorHostList(sponsorHosts: any[] | undefined, ctx: ResolveSponsorCtx): string {
  if (!sponsorHosts || !Array.isArray(sponsorHosts) || sponsorHosts.length === 0) {
    return "";
  }
  return sponsorHosts
    .map((s) => resolveSponsorEntryLabel(s, ctx))
    .filter((s): s is string => Boolean(s && String(s).trim() !== ""))
    .join(", ");
}

/** Host/Sponsor column: pure WSG-hosted events have no sponsorHost rows but should still read "WSG". */
function formatStaffHostSponsorColumn(
  sponsorHosts: any[] | undefined,
  eventHostSponsor: string | undefined,
  ctx: ResolveSponsorCtx
): string {
  const resolved = formatSponsorHostList(sponsorHosts, ctx);
  if (eventHostSponsor === "wsg") {
    return resolved ? `${resolved}, WSG` : "WSG";
  }
  return resolved;
}

type SponsorHostUsage = {
  memberIds: Set<string>;
  wsgGroupIds: Set<string>;
};

function PeopleIcon({ className = "w-5 h-5 flex-shrink-0" }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function AttendeesCountButton({
  count,
  onOpen,
  title,
}: {
  count: number;
  onOpen: () => void;
  title: string;
}) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
      title={title}
    >
      <span className="relative inline-flex h-6 w-6 flex-shrink-0 items-center justify-center">
        <PeopleIcon
          className={`pointer-events-none h-6 w-6 transition-colors ${
            hover ? "!text-blue-300" : "!text-[#D2D8DD]"
          }`}
        />
        <span
          className={`absolute inset-0 flex items-center justify-center text-[14px] font-semibold tabular-nums leading-none transition-colors ${
            hover ? "!text-secondary" : "!text-gray-900"
          }`}
        >
          {count}
        </span>
      </span>
    </button>
  );
}

export default function EventList(): React.ReactElement {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const endpoint = '/event';

  const handleAttendeesClick = (event: IEvent) => {
    dispatch(ModifyEvent(event))
    navigate(`/events-staff/${event.id}/attendees`)
  }

  const { regionData } = UseRegionList();
  const { countryData } = UseCountryList();

  const [firmData, setFirmData] = useState<any[]>([]);
  const [allGroups, setAllGroups] = useState<any[]>([]);
  const [allIndustries, setAllIndustries] = useState<any[]>([]);
  const [sponsorHostUsage, setSponsorHostUsage] = useState<SponsorHostUsage | null>(null);

  useEffect(() => {
    const loadAllData = async () => {
      try {
        const [firms, groups, industries, usageRes] = await Promise.all([
          getAllFirmNames(),
          getAllGroups(),
          getAllPracticeIndustries(),
          listEventsAxios
            .Get("./event?distinctSponsorHosts=true")
            .then((r) => r?.data)
            .catch(() => null),
        ]);
        setFirmData(firms);
        setAllGroups(groups);
        setAllIndustries(industries);

        const u = usageRes as { memberIds?: string[]; wsgGroupIds?: string[] } | null;
        if (u && typeof u === "object") {
          setSponsorHostUsage({
            memberIds: new Set((u.memberIds ?? []).map(String)),
            wsgGroupIds: new Set((u.wsgGroupIds ?? []).map(String)),
          });
        } else {
          setSponsorHostUsage({
            memberIds: new Set(),
            wsgGroupIds: new Set(),
          });
        }
      } catch (error) {
        setSponsorHostUsage({
          memberIds: new Set(),
          wsgGroupIds: new Set(),
        });
      }
    };

    loadAllData();
  }, []);

  const getRegionNames = (regionIds: string[]) => {
    if (!regionIds || !regionData) {
      return "No regions";
    }
    return regionIds
      .map((id) => regionData.find((region) => region.id === id)?.name)
      .filter(Boolean)
      .join(", ");
  };

  const SponsorCell = function SponsorCell({
    sponsorHosts,
    eventHostSponsor,
  }: {
    sponsorHosts: any[];
    eventHostSponsor?: string;
  }) {
    const text = useMemo(
      () =>
        formatStaffHostSponsorColumn(sponsorHosts, eventHostSponsor, {
          firmData: Array.isArray(firmData) ? firmData : [],
          allGroups: Array.isArray(allGroups) ? allGroups : [],
          allIndustries: Array.isArray(allIndustries) ? allIndustries : [],
        }),
      [sponsorHosts, eventHostSponsor, firmData, allGroups, allIndustries]
    );
    return <span>{text}</span>;
  };

  const columnsDef = useMemo(() => [
    {
      id: "edit",
      header: "Edit",
      enableSorting: false,
      cell: ({ row }: { row: any; value: any }) => (
        <PersistentLink
          className="flex items-center"
          to={`${row.id}`}
          onClick={() => dispatch(NewEvent(row._apiEvent ?? row))}
        >
          <div className="w-4 h-4">
            <ShapeEdit />
          </div>
        </PersistentLink>
      ),
    },
    { id: "shortId", header: "Event ID" },
    { id: "title", header: "Title", width: 180 },
    { id: "eventType", header: "Event Type" },
    { id: "setupType", header: "Setup" },
    { id: "location", header: "Location" },
    { id: "startDate", header: "Start Date" },
    {
      id: "eventHostSponsor",
      header: "Host/Sponsor",
      enableSorting: false,
      cell: ({ row }: { rowIndex: number; row: any; value: any }) => (
        <SponsorCell
          sponsorHosts={row.sponsorHost || []}
          eventHostSponsor={row.eventHostSponsor}
        />
      ),
    },
    { id: "regions", header: "Regions" },
    {
      id: "status",
      header: "Status",
      cell: ({ row }: { rowIndex: number; row: any; value: any }) => {
        const raw = row.status;
        const fullLabel = typeof raw === "string" && (StatusEnum as any)[raw] != null
          ? (StatusEnum as any)[raw]
          : raw ?? "No status";
        const statusDisplayAlias: Record<string, string> = {
          [StatusEnum.private]: "Private",
          [StatusEnum.online]: "Live",
          [StatusEnum.offline]: "Offline",
        };
        const display = statusDisplayAlias[fullLabel] ?? fullLabel;
        return <span>{display}</span>;
      },
    },
    {
      id: "attendees",
      header: "Att",
      width: 80,
      enableSorting: false,
      cell: ({ row }: { rowIndex: number; row: any; value: any }) => (
        <AttendeesCountButton
          count={row.attendees || 0}
          onOpen={() => handleAttendeesClick((row._apiEvent ?? row) as IEvent)}
          title="View attendees (all except cancelled, deleted, WSG Staff)"
        />
      ),
    },
  ], [navigate, dispatch, firmData, allGroups, allIndustries]);

  const eventTypeOptions = Object.entries(EventTypeEnum).map(([id, name]) => ({
    id,
    name,
  }));

  const hostSponsorOptions = Object.entries(EventHostSponsorEnum).map(([id, name]) => ({
    id,
    name,
  }));

  const hostSponsorNameOptions = React.useMemo(() => {
    if (sponsorHostUsage == null) {
      return [];
    }
    const { memberIds, wsgGroupIds } = sponsorHostUsage;
    const out: { id: string; name: string }[] = [];
    const firms = Array.isArray(firmData) ? firmData : [];
    for (const f of firms) {
      if (f?.id && memberIds.has(String(f.id))) {
        out.push({
          id: `member:${f.id}`,
          name: firmShortLabel(f) || String(f.id),
        });
      }
    }
    for (const g of allGroups) {
      if (g?.id && wsgGroupIds.has(String(g.id))) {
        out.push({ id: `wsg_group:${g.id}`, name: String(g.title ?? g.id) });
      }
    }
    return out.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );
  }, [firmData, allGroups, sponsorHostUsage]);

  const setupTypeOptions = Object.entries(SetupTypeEnum).map(([id, name]) => ({
    id,
    name,
  }));


  const yearOptions = React.useMemo(() => getEventYearFilterOptions(), []);

  const statusOptions = React.useMemo(
    () =>
      Object.entries(StatusEnum).map(([id, name]) => ({
        id,
        name,
      })),
    []
  );

  const handleAddEvent = () => {
    navigate(`Create`);
    dispatch(ResetEvent());
  }
  
  const filtersData: IFilterData[] = React.useMemo(
    () => [
      {
        name: "Year",
        customTitle: "By Year:",
        fieldName: "year",
        type: "select",
        comparison: "EQUAL",
        objectData: yearOptions,
        sortByName: false,
      },
      {
        name: "Region",
        fieldName: "regionIds",
        objectData: regionData || [],
        comparison: "BY_ID",
      },
      {
        name: "Country",
        fieldName: "address.countryName",
        objectData:
          countryData?.map((c: any) => ({ id: c.id, name: c.name })) || [],
        comparison: "BY_NAME",
      },
      {
        name: "Event Type",
        fieldName: "eventType",
        objectData: eventTypeOptions,
        comparison: "EQUAL",
      },
      {
        name: "Setup Type",
        fieldName: "setupType",
        objectData: setupTypeOptions,
        comparison: "EQUAL",
      },

      {
        name: "Sponsorship Type",
        fieldName: "eventHostSponsor",
        objectData: hostSponsorOptions,
        comparison: "EQUAL",
      },
      {
        name: "HostSponsorName",
        customTitle: "Host/Sponsor:",
        fieldName: "sponsorHostName",
        type: "multiselect",
        objectData: hostSponsorNameOptions,
        comparison: "SPONSOR_NAME_TOKENS",
        sortByName: false,
      },
      {
        name: "Status",
        fieldName: "status",
        type: "multiselect",
        comparison: "EQUAL",
        objectData: statusOptions,
      },
      {
        name: "Show Member Events",
        fieldName: "memberEventsTab",
        objectData: [
          { id: "No", name: "No" },
          { id: "Yes", name: "Yes" },
        ],
        comparison: "BOOLEAN",
        disabledWrite: true,
      }, 
    ],
    [yearOptions, regionData, countryData, eventTypeOptions, setupTypeOptions, hostSponsorOptions, hostSponsorNameOptions, statusOptions]
  );

  const formatData = (data: IEvent[]) => {
    if (!data || !Array.isArray(data)) {
      return [];
    }
    return data.map((d: IEvent) => ({
      ...d,
      /** Fila sin formatear (API); usar para Redux/navegación — `row` sobrescribe campos para la tabla */
      _apiEvent: d,
      eventType: EventTypeEnum[d.eventType as keyof typeof EventTypeEnum] || d.eventType,
      setupType: getSetupTypeDisplay(d.setupType) || "",
      startDate: d.comingSoonDate
        ? formatEventDate(d, true)
        : d.startDate
          ? formatUnixTimestampToUtcDateString(d.startDate)
          : "TBD",
      endDate: d.comingSoonDate
        ? ""
        : d.endDate
          ? formatUnixTimestampToUtcDateString(d.endDate)
          : "TBD",
      regions: getRegionNames(d.regionIds || []) || "No regions",
      status: StatusEnum[d.status as keyof typeof StatusEnum],
      location: formatEventLocation(d, true)
    }));
  };

  const formatExcelData = async (data: IEvent[], isCurrentData?: boolean): Promise<any[]> => {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    const sponsorCtx: ResolveSponsorCtx = {
      firmData: Array.isArray(firmData) ? firmData : [],
      allGroups: Array.isArray(allGroups) ? allGroups : [],
      allIndustries: Array.isArray(allIndustries) ? allIndustries : [],
    };

    return data.map((d: IEvent) => {
      const hostSponsorColumn = formatStaffHostSponsorColumn(
        d.sponsorHost,
        d.eventHostSponsor,
        sponsorCtx
      );

      return {
        ...d,
        eventType: EventTypeEnum[d.eventType as keyof typeof EventTypeEnum] || d.eventType,
        setupType: getSetupTypeDisplay(d.setupType) || "",
        startDate: d.comingSoonDate
          ? formatEventDate(d, true)
          : d.startDate
            ? formatUnixTimestampToUtcDateString(d.startDate)
            : undefined,
        endDate: d.comingSoonDate
          ? ""
          : d.endDate
            ? formatUnixTimestampToUtcDateString(d.endDate)
            : undefined,
        regions: getRegionNames(d.regionIds || []),
        status: StatusEnum[d.status as keyof typeof StatusEnum],
        location: isVirtualLocationEvent(d)
          ? "Virtual"
          : d.address
            ? `${d.address.cityName}, ${d.address.countryName}`
            : "",
        eventHostSponsor: hostSponsorColumn,
      };
    });
  };
  
  const quickFilters: IQuickFilter[] = React.useMemo((): IQuickFilter[] => {
    return [
      {
        key: "upcomingLive",
        label: "Upcoming All",
        panelValues: {
          Year: [
            {
              id: EVENT_YEAR_FILTER_UPCOMING_ALL,
              name: "Upcoming - all",
            },
          ],
        },
      },
      {
        key: "pastLive",
        label: "Past All",
        panelValues: {
          Year: [
            {
              id: EVENT_YEAR_FILTER_PAST_ALL,
              name: "Past - all",
            },
          ],
        },
      },
      {
        key: "allEvents",
        label: "All Events",
        hiddenQuery: {
          eventTbdSort: { value: true },
        },
      },
    ];
  }, []);

  const upcomingLiveDefaultSorting = { field: "startDate", order: "asc" as const };
  const startDateDescSorting = { field: "startDate", order: "desc" as const };

  const coerceQuickFilterOnYearChange = React.useCallback(
    (yearId: string | null | undefined, currentQuickFilter: string | null) => {
      const hint = getEventYearFilterTimingHint(yearId);
      if (!hint || !currentQuickFilter) {
        return null;
      }
      if (hint === "past" && currentQuickFilter === "upcomingLive") {
        return "pastLive";
      }
      if (hint === "upcoming" && currentQuickFilter === "pastLive") {
        return "upcomingLive";
      }
      return null;
    },
    [],
  );

  return (
    <>
      <FilteredTableCore
        columnsDef={columnsDef}
        showRowNumber
        endpoint={endpoint}
        filtersData={filtersData}
        formatData={formatData}
        formatExcelData={formatExcelData}
        quickFilters={quickFilters}
        mergePanelFiltersIntoPersistentFilter
        coerceQuickFilterOnYearChange={coerceQuickFilterOnYearChange}
        quickFilterRequireSelection
        defaultSorting={upcomingLiveDefaultSorting}
        quickFilterDefaultSorting={{
          upcomingLive: upcomingLiveDefaultSorting,
          pastLive: startDateDescSorting,
          allEvents: startDateDescSorting,
        }}
        applyQuickFilterSortOnChipChange
        defaultPageSize={200}
        persistPageSize={false}
        showSearchInput
        searchPlaceholder="Search by Title, Event ID or Location"
        searchToolbarAccessory={
          <Button
            text="Add Event"
            color="secondary"
            variant="filled"
            size="fixed"
            onClick={handleAddEvent}
            icon={<Plus />}
          />
        }
      />
    </>
  );
}